import type { EventToCompare } from "../models/Event";

const DRAFT_EVENT_ACTION = {
	CREATED: "C",
	UPDATED: "U",
	DELETED: "D",
	MOVED: "M",
} as const;

type DraftEventAction =
	(typeof DRAFT_EVENT_ACTION)[keyof typeof DRAFT_EVENT_ACTION];

export interface DraftDivergenceSummary {
	created: number;
	deleted: number;
	updated: number;
	moved: number;
	total: number;
}

const emptySummary: DraftDivergenceSummary = {
	created: 0,
	deleted: 0,
	updated: 0,
	moved: 0,
	total: 0,
};

const hasAction = (event: EventToCompare, action: DraftEventAction): boolean =>
	event.ActionTypeCode === action;

export const getDraftDivergenceSummary = ({
	mainEvents,
	draftEvents,
}: {
	mainEvents: EventToCompare[];
	draftEvents: EventToCompare[];
}): DraftDivergenceSummary => {
	const affectedEventIds = new Set<number>();

	const created = draftEvents.filter((event) =>
		hasAction(event, DRAFT_EVENT_ACTION.CREATED),
	).length;
	const deleted = mainEvents.filter((event) =>
		hasAction(event, DRAFT_EVENT_ACTION.DELETED),
	).length;
	const updated = draftEvents.filter((event) =>
		hasAction(event, DRAFT_EVENT_ACTION.UPDATED),
	).length;
	const moved = draftEvents.filter((event) =>
		hasAction(event, DRAFT_EVENT_ACTION.MOVED),
	).length;

	draftEvents.forEach((event) => {
		if (
			event.ActionTypeCode === DRAFT_EVENT_ACTION.CREATED ||
			event.ActionTypeCode === DRAFT_EVENT_ACTION.UPDATED ||
			event.ActionTypeCode === DRAFT_EVENT_ACTION.MOVED
		) {
			affectedEventIds.add(event.EventId);
		}
	});

	mainEvents.forEach((event) => {
		if (event.ActionTypeCode === DRAFT_EVENT_ACTION.DELETED) {
			affectedEventIds.add(event.EventId);
		}
	});

	return {
		...emptySummary,
		created,
		deleted,
		updated,
		moved,
		total: affectedEventIds.size,
	};
};
